import { useEffect, useState, useCallback } from "react";
import { T } from "@/components/T";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { searchAll } from "@/lib/api";
import { itemPath, CATEGORY_BY_TYPE } from "@/lib/categories";
import KnowledgeCard from "@/components/KnowledgeCard";
import { Search, BookMarked } from "lucide-react";

export default function SearchPage() {
  const { t } = useLang();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [results, setResults] = useState({ content: [], glossary: [], pages: [] });
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const run = useCallback((query) => {
    if (!query.trim()) {
      setResults({ content: [], glossary: [], pages: [] });
      setSearched(false);
      return;
    }
    setLoading(true);
    searchAll(query)
      .then((d) => { setResults(d); setSearched(true); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const query = params.get("q") || "";
    setQ(query);
    run(query);
  }, [params, run]);

  const submit = (e) => {
    e.preventDefault();
    setParams(q.trim() ? { q: q.trim() } : {});
  };

  return (
    <div className="mx-auto max-w-5xl px-5 py-14 md:px-8 md:py-20">
      <p className="overline-label mb-3"><T>Search Dharmartha</T></p>
      <form onSubmit={submit} className="flex items-center gap-3 border-b-2 border-line pb-2 transition-colors focus-within:border-saffron">
        <Search className="h-6 w-6 text-saffron" />
        <input
          data-testid="search-page-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("search_placeholder")}
          className="w-full bg-transparent py-2 font-deva-body text-2xl text-ink placeholder:text-ink-3 focus:outline-none"
          autoFocus
        />
      </form>

      {loading && <p className="py-16 text-center text-ink-3">…</p>}

      {!loading && searched && results.content.length === 0 && results.glossary.length === 0 && (results.pages || []).length === 0 && (
        <p className="py-16 text-center text-ink-3">{t("no_results")}</p>
      )}

      {(results.pages || []).length > 0 && (
        <section className="mt-12">
          <p className="overline-label mb-6 text-ink-3"><T>Deities, temples & knowledge</T></p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {results.pages.map((p) => (
              <Link key={p.kind + p.path} data-testid="search-page-result" to={p.path} className="flex items-center justify-between gap-3 border border-line bg-surface p-5 transition-colors hover:border-[var(--gold-muted)]">
                <div className="min-w-0">
                  {p.deva && <p className="font-deva text-xl text-ink deva-lh">{p.deva}</p>}
                  <p className="truncate text-sm text-ink-2">{p.title}</p>
                </div>
                <span className="shrink-0 border border-line px-2 py-0.5 text-[10px] uppercase tracking-wide text-ink-3">{p.kind}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {results.content.length > 0 && (
        <section className="mt-12">
          <p className="overline-label mb-6 text-ink-3">{results.content.length} <T>entries</T></p>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {results.content.map((it) => (
              <KnowledgeCard key={it.slug} item={it} />
            ))}
          </div>
        </section>
      )}

      {results.glossary.length > 0 && (
        <section className="mt-14">
          <p className="overline-label mb-6 text-ink-3"><T>Glossary</T></p>
          <div className="grid gap-4 sm:grid-cols-2">
            {results.glossary.map((g) => (
              <Link key={g.id} to={`/glossary?q=${encodeURIComponent(g.transliteration)}`} className="flex items-start gap-3 border border-line bg-surface p-5 transition-colors hover:border-[var(--gold-muted)]">
                <BookMarked className="mt-1 h-4 w-4 shrink-0 text-gold" />
                <div>
                  <p className="font-deva text-xl text-ink">{g.devanagari} <span className="text-sm text-gold">— {g.transliteration}</span></p>
                  <p className="mt-1 text-sm text-ink-2">{g.meaning}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
